/**
 * 设备信息获取工具
 * 用于生成设备指纹和获取设备信息，支持单点登录功能
 */

/**
 * 生成设备ID
 * 基于浏览器指纹生成相对稳定的设备标识
 */
export const generateDeviceId = () => {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('Device fingerprint', 2, 2);
    
    const fingerprint = [
      navigator.userAgent,
      navigator.language,
      window.screen.width + 'x' + window.screen.height,
      window.screen.colorDepth,
      new Date().getTimezoneOffset(),
      canvas.toDataURL()
    ].join('|');
    
    // 简单哈希函数生成设备ID
    let hash = 0;
    for (let i = 0; i < fingerprint.length; i++) {
      const char = fingerprint.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // 转为32位整数
    }
    
    return 'device_' + Math.abs(hash).toString(36);
  } catch (error) {
    console.warn('生成设备ID失败，使用随机ID:', error);
    return 'device_' + Math.random().toString(36).substr(2, 9);
  }
};

/**
 * 获取浏览器信息
 */
export const getBrowserInfo = () => {
  const userAgent = navigator.userAgent;
  
  // 检测浏览器类型（注意检测顺序很重要）
  let browserName = 'Unknown';
  let browserVersion = 'Unknown';
  
  // 先检测Edge（包括新版Edge和老版Edge）
  if (userAgent.indexOf('Edg/') > -1) {
    browserName = 'Edge';
    const match = userAgent.match(/Edg\/(\d+)/);
    if (match) browserVersion = match[1];
  } else if (userAgent.indexOf('Edge/') > -1) {
    browserName = 'Edge Legacy';
    const match = userAgent.match(/Edge\/(\d+)/);
    if (match) browserVersion = match[1];
  }
  // 检测Chrome（必须在Safari之前检测，因为Chrome也包含Safari字符串）
  else if (userAgent.indexOf('Chrome/') > -1) {
    browserName = 'Chrome';
    const match = userAgent.match(/Chrome\/(\d+)/);
    if (match) browserVersion = match[1];
  }
  // 检测Firefox
  else if (userAgent.indexOf('Firefox/') > -1) {
    browserName = 'Firefox';
    const match = userAgent.match(/Firefox\/(\d+)/);
    if (match) browserVersion = match[1];
  }
  // 检测Safari（必须在最后检测，因为其他浏览器可能也包含Safari字符串）
  else if (userAgent.indexOf('Safari/') > -1 && userAgent.indexOf('Chrome') === -1) {
    browserName = 'Safari';
    const match = userAgent.match(/Version\/(\d+)/);
    if (match) browserVersion = match[1];
  }
  // 检测Opera
  else if (userAgent.indexOf('OPR/') > -1) {
    browserName = 'Opera';
    const match = userAgent.match(/OPR\/(\d+)/);
    if (match) browserVersion = match[1];
  }
  // 检测其他浏览器
  else if (userAgent.indexOf('UCBrowser/') > -1) {
    browserName = 'UC Browser';
    const match = userAgent.match(/UCBrowser\/(\d+)/);
    if (match) browserVersion = match[1];
  }
  
  return {
    name: browserName,
    version: browserVersion,
    userAgent: userAgent
  };
};

/**
 * 获取操作系统信息
 */
export const getOSInfo = () => {
  const userAgent = navigator.userAgent;
  const platform = navigator.platform || '';
  
  let osName = 'Unknown';
  let osVersion = 'Unknown';
  
  // 优先检测移动设备操作系统
  if (userAgent.indexOf('iPhone') > -1) {
    osName = 'iOS';
    const match = userAgent.match(/OS (\d+_\d+)/);
    if (match) osVersion = match[1].replace('_', '.');
  } else if (userAgent.indexOf('iPad') > -1) {
    osName = 'iPadOS';
    const match = userAgent.match(/OS (\d+_\d+)/);
    if (match) osVersion = match[1].replace('_', '.');
  } else if (userAgent.indexOf('Android') > -1) {
    osName = 'Android';
    const match = userAgent.match(/Android (\d+\.?\d*)/);
    if (match) osVersion = match[1];
  }
  // 检测桌面操作系统
  else if (userAgent.indexOf('Windows') > -1) {
    osName = 'Windows';
    if (userAgent.indexOf('Windows NT 10.0') > -1) osVersion = '10';
    else if (userAgent.indexOf('Windows NT 6.3') > -1) osVersion = '8.1';
    else if (userAgent.indexOf('Windows NT 6.2') > -1) osVersion = '8';
    else if (userAgent.indexOf('Windows NT 6.1') > -1) osVersion = '7';
    else if (userAgent.indexOf('Windows NT 6.0') > -1) osVersion = 'Vista';
    else if (userAgent.indexOf('Windows NT 5.1') > -1) osVersion = 'XP';
  } else if (userAgent.indexOf('Mac OS X') > -1) {
    osName = 'macOS';
    const match = userAgent.match(/Mac OS X (\d+[._]\d+)/);
    if (match) osVersion = match[1].replace('_', '.');
  } else if (userAgent.indexOf('Linux') > -1) {
    osName = 'Linux';
    // 进一步细分Linux发行版
    if (userAgent.indexOf('Ubuntu') > -1) osName = 'Ubuntu';
    else if (userAgent.indexOf('CentOS') > -1) osName = 'CentOS';
    else if (userAgent.indexOf('Red Hat') > -1) osName = 'Red Hat';
  }
  // 检测其他移动操作系统
  else if (userAgent.indexOf('BlackBerry') > -1 || userAgent.indexOf('BB10') > -1) {
    osName = 'BlackBerry';
  } else if (userAgent.indexOf('Windows Phone') > -1) {
    osName = 'Windows Phone';
    const match = userAgent.match(/Windows Phone (\d+\.\d+)/);
    if (match) osVersion = match[1];
  } else if (userAgent.indexOf('webOS') > -1) {
    osName = 'webOS';
  }
  // 通过platform进一步检测
  else if (platform.indexOf('Win') > -1) {
    osName = 'Windows';
  } else if (platform.indexOf('Mac') > -1) {
    osName = 'macOS';
  } else if (platform.indexOf('Linux') > -1) {
    osName = 'Linux';
  }
  
  return {
    name: osName,
    version: osVersion
  };
};

/**
 * 获取屏幕信息
 */
export const getScreenInfo = () => {
  const devicePixelRatio = window.devicePixelRatio || 1;
  
  return {
    // CSS分辨率（逻辑分辨率）
    width: window.screen.width,
    height: window.screen.height,
    colorDepth: window.screen.colorDepth,
    pixelDepth: window.screen.pixelDepth,
    availWidth: window.screen.availWidth,
    availHeight: window.screen.availHeight,
    
    // 物理分辨率（真实分辨率）
    physicalWidth: Math.round(window.screen.width * devicePixelRatio),
    physicalHeight: Math.round(window.screen.height * devicePixelRatio),
    devicePixelRatio: devicePixelRatio,
    
    // 可用区域的物理分辨率
    physicalAvailWidth: Math.round(window.screen.availWidth * devicePixelRatio),
    physicalAvailHeight: Math.round(window.screen.availHeight * devicePixelRatio)
  };
};

/**
 * 获取完整的设备信息
 */
export const getDeviceInfo = () => {
  const browser = getBrowserInfo();
  const os = getOSInfo();
  const screen = getScreenInfo();
  
  return {
    deviceId: generateDeviceId(),
    browser: browser,
    os: os,
    screen: screen,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: navigator.language,
    languages: navigator.languages,
    timestamp: new Date().toISOString(),
    cookieEnabled: navigator.cookieEnabled,
    onLine: navigator.onLine
  };
};

/**
 * 获取简化的设备标识（用于显示）
 */
export const getDeviceDisplayName = () => {
  const browser = getBrowserInfo();
  const os = getOSInfo();
  
  return `${browser.name} ${browser.version} on ${os.name} ${os.version}`.trim();
};

/**
 * 检测是否为移动设备
 */
export const isMobileDevice = () => {
  const userAgent = navigator.userAgent;
  
  // 检测移动设备的正则表达式
  const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i;
  
  // 通过User-Agent检测
  if (mobileRegex.test(userAgent)) {
    return true;
  }
  
  // 通过屏幕尺寸检测（作为辅助判断）
  if (window.screen && window.screen.width <= 768) {
    return true;
  }
  
  // 通过触摸检测
  if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
    // 但是要排除触摸屏笔记本电脑
    if (window.screen && window.screen.width > 1024) {
      return false;
    }
    return true;
  }
  
  return false;
};

/**
 * 获取网络信息（如果支持）
 */
export const getNetworkInfo = () => {
  if ('connection' in navigator) {
    const connection = navigator.connection;
    return {
      effectiveType: connection.effectiveType || 'unknown',
      downlink: connection.downlink || 0,
      rtt: connection.rtt || 0,
      saveData: connection.saveData || false
    };
  }
  return null;
};

/**
 * 获取用于登录的设备信息
 * 包含设备ID和基本信息，用于后端识别和管理
 * 符合异地登录风险评估系统要求的格式
 */
export const getLoginDeviceInfo = () => {
  const deviceInfo = getDeviceInfo();
  const deviceType = getDeviceType();
  const networkInfo = getNetworkInfo();
  
  return {
    deviceId: deviceInfo.deviceId,
    
    // 基本设备信息
    userAgent: deviceInfo.browser.userAgent,
    platform: navigator.platform || 'Unknown',
    browser: `${deviceInfo.browser.name}`,
    os: `${deviceInfo.os.name}`,
    deviceType: deviceType,
    displayName: getDeviceDisplayName(),
    isMobile: isMobileDevice(),
    
    // 浏览器详细信息
    browserInfo: {
      name: deviceInfo.browser.name,
      version: deviceInfo.browser.version,
      userAgent: deviceInfo.browser.userAgent
    },
    
    // 操作系统详细信息
    osInfo: {
      name: deviceInfo.os.name,
      version: deviceInfo.os.version
    },
    
    // 屏幕信息
    screenInfo: {
      width: deviceInfo.screen.width,
      height: deviceInfo.screen.height,
      colorDepth: deviceInfo.screen.colorDepth,
      pixelDepth: deviceInfo.screen.pixelDepth,
      availWidth: deviceInfo.screen.availWidth,
      availHeight: deviceInfo.screen.availHeight
    },
    screen: `${deviceInfo.screen.width}x${deviceInfo.screen.height}`,
    
    // 其他信息
    timezone: deviceInfo.timezone || 'Asia/Shanghai',
    language: deviceInfo.language || 'zh-CN',
    languages: deviceInfo.languages || [deviceInfo.language || 'zh-CN'],
    cookieEnabled: deviceInfo.cookieEnabled,
    onLine: deviceInfo.onLine,
    
    // 网络信息（如果支持）
    network: networkInfo,
    
    timestamp: deviceInfo.timestamp
  };
};

/**
 * 获取详细的设备类型
 */
export const getDeviceType = () => {
  const userAgent = navigator.userAgent;
  const screenWidth = window.screen ? window.screen.width : 0;
  
  // 检测手机
  if (/Android.*Mobile|iPhone|Windows Phone|BlackBerry|BB10|Mobile/i.test(userAgent)) {
    return 'Mobile';
  }
  
  // 检测平板
  if (/iPad|Android(?!.*Mobile)|Tablet/i.test(userAgent)) {
    return 'Tablet';
  }
  
  // 通过屏幕尺寸进一步判断
  if (screenWidth > 0) {
    if (screenWidth <= 480) {
      return 'Mobile';
    } else if (screenWidth <= 1024) {
      // 进一步检查是否是平板
      if (/Android|iPad/i.test(userAgent) || 'ontouchstart' in window) {
        return 'Tablet';
      }
    }
  }
  
  // 检测智能电视
  if (/TV|SmartTV|SMART-TV|NetCast|HbbTV|AppleTV|GoogleTV|Android TV/i.test(userAgent)) {
    return 'TV';
  }
  
  // 检测游戏机
  if (/PlayStation|Xbox|Nintendo/i.test(userAgent)) {
    return 'Console';
  }
  
  // 检测可穿戴设备
  if (/Watch|wearable/i.test(userAgent)) {
    return 'Wearable';
  }
  
  // 默认为桌面设备
  return 'Desktop';
};